/* eslint-disable react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { Watchlist } from '../../types';
import {
  ChangeEvent,
  ClipboardEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useRecoilValue } from 'recoil';
import {
  equitySymbolsState,
  UNLOADED_EQUITIES_DEFAULT_LENGTH,
  hiroSymbolsState,
} from '../../states';
import { alpha, useTheme } from '@mui/material/styles';
import { uniq } from 'lodash';
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { improvedFilterOptions } from 'util/shared/common';

// Delimiters for splitting pasted/typed symbol lists
const SYMBOL_DELIMITERS = /[,;\s\n\r\t]+/;

type EditWatchlistProps = {
  watchlist: Watchlist;
  onChange: (newWatchlist: Watchlist | null) => void;
  modal?: boolean;
};

const DraggableSymbol: React.FC<{
  id: string;
  symbol: string;
  onDelete: (symbol: string) => void;
}> = ({ id, symbol, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        margin: '3px',
        display: 'inline-flex',
      }}
      {...attributes}
      {...listeners}
    >
      <Chip
        label={symbol}
        onDelete={() => onDelete(symbol)}
        variant="outlined"
        size="small"
        sx={{
          cursor: 'grab',
          userSelect: 'none',
          '&:hover': {
            borderColor: 'primary.main',
          },
        }}
      />
    </Box>
  );
};

export const EditWatchlist = ({
  watchlist,
  onChange,
  modal,
}: EditWatchlistProps) => {
  const [allSymbols, setAllSymbols] = useState<string[]>([]);
  const [watchlistSyms, setWatchlistSyms] = useState(watchlist.symbols);
  const [name, setName] = useState(watchlist.name);
  const [query, setQuery] = useState('');
  const [invalidInput, setInvalidInput] = useState('');
  const equities = useRecoilValue(equitySymbolsState);
  const hiroSyms = useRecoilValue(hiroSymbolsState);
  const theme = useTheme();

  const allSymbolsSet = useMemo(() => new Set(allSymbols), [allSymbols]);

  // Auto-clear invalid input feedback after 3s
  useEffect(() => {
    if (!invalidInput) {
      return;
    }
    const timer = setTimeout(() => setInvalidInput(''), 3_000);
    return () => clearTimeout(timer);
  }, [invalidInput]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = watchlistSyms.indexOf(active.id as string);
      const newIndex = watchlistSyms.indexOf(over.id as string);

      const reorderedSymbols = arrayMove(
        [...watchlistSyms],
        oldIndex,
        newIndex,
      );
      setWatchlistSyms(reorderedSymbols);
    }
  };

  /** Parse raw text into symbols, validate against known symbols, and add valid new ones */
  const parseAndAddSymbols = useCallback(
    (raw: string) => {
      const candidates = raw
        .split(SYMBOL_DELIMITERS)
        .map((s) => s.trim().toUpperCase())
        .filter((s) => s.length > 0);

      const existingSet = new Set(watchlistSyms);
      const validNew = new Set(
        candidates.filter((s) => allSymbolsSet.has(s) && !existingSet.has(s)),
      );
      const invalid = candidates.filter(
        (s) => !allSymbolsSet.has(s) && !existingSet.has(s),
      );

      if (validNew.size > 0) {
        setWatchlistSyms((prev) => [...prev, ...validNew]);
      }

      if (invalid.length > 0) {
        setInvalidInput(invalid.join(', '));
      } else {
        setInvalidInput('');
      }
    },
    [watchlistSyms, allSymbolsSet],
  );

  /** Intercept paste events to handle comma/space/newline-separated symbol lists */
  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      const pastedText = event.clipboardData.getData('text');
      if (SYMBOL_DELIMITERS.test(pastedText)) {
        event.preventDefault();
        event.stopPropagation();
        parseAndAddSymbols(pastedText);
        setQuery('');
      }
    },
    [parseAndAddSymbols],
  );

  useEffect(() => {
    onChange({
      name,
      id: watchlist.id,
      symbols: watchlistSyms,
      soundDisabled: watchlist.soundDisabled,
    });
  }, [name, watchlistSyms]);

  useEffect(() => {
    const hiroSymbols = [...hiroSyms.values()];
    const ehSymbols = new Set(equities.map((d) => d.symbol));
    const allSyms = uniq([...hiroSymbols, ...ehSymbols.values()]);

    if (allSyms.length != allSymbols.length) {
      // if both equities and hiroSyms have loaded
      if (
        ehSymbols.size > UNLOADED_EQUITIES_DEFAULT_LENGTH &&
        hiroSyms.size > 0
      ) {
        const filteredWatchlistSyms = watchlistSyms.filter(
          (s) => hiroSyms.has(s) || ehSymbols.has(s),
        );
        if (watchlistSyms.length > filteredWatchlistSyms.length) {
          setWatchlistSyms(filteredWatchlistSyms);
        }
      }
      setAllSymbols(allSyms);
    }
  }, [hiroSyms, equities, watchlistSyms]);

  return (
    <Box
      sx={{
        width: modal ? '100%' : 'auto',
        p: 3,
        borderRadius: '4px',
        backgroundColor: alpha(theme.palette.primary.main, 0.05),
      }}
    >
      <Box display="flex" flexDirection="column" width={1}>
        <Box display="flex" alignItems="center" mb={1} gap={3}>
          <TextField
            value={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setName(e.target.value)
            }
            variant="standard"
            size="small"
            sx={{
              '& .MuiInput-input': {
                fontWeight: 600,
                fontSize: '1rem',
                color: theme.palette.text.primary,
              },
              '& .MuiInput-root:before': {
                borderBottomColor: alpha(theme.palette.primary.main, 0.35),
              },
              '& .MuiInput-root:hover:not(.Mui-disabled):before': {
                borderBottomColor: theme.palette.primary.main,
              },
              '& .MuiInput-root:after': {
                borderBottomColor: theme.palette.primary.main,
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{ ml: 1.5, color: 'text.secondary', whiteSpace: 'nowrap' }}
          >
            {watchlistSyms.length} symbol
            {watchlistSyms.length !== 1 ? 's' : ''}
          </Typography>
          <Box flex={1} />
          <IconButton
            data-ph-capture-attribute-element="watchlist-symbol-delete"
            aria-label={`delete ${name}`}
            onClick={(_event) => onChange(null)}
            color="error"
            size="small"
            sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={watchlistSyms}
            strategy={verticalListSortingStrategy}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                minHeight: '36px',
                gap: '2px',
              }}
            >
              {watchlistSyms.map((symbol) => (
                <DraggableSymbol
                  key={symbol}
                  id={symbol}
                  symbol={symbol}
                  onDelete={(deletedSymbol) => {
                    const newSymbols = watchlistSyms.filter(
                      (s) => s !== deletedSymbol,
                    );
                    setWatchlistSyms(newSymbols);
                  }}
                />
              ))}

              <Autocomplete
                sx={{
                  flexGrow: 1,
                  minWidth: '180px',
                  mr: theme.spacing(1),
                  [theme.breakpoints.down('md')]: { width: '100%' },
                }}
                freeSolo
                autoHighlight
                clearOnBlur
                disableClearable={true}
                filterOptions={improvedFilterOptions}
                filterSelectedOptions={true}
                multiple
                value={watchlistSyms}
                inputValue={query}
                options={query.length === 0 ? [] : allSymbols}
                getOptionLabel={(option) => option}
                defaultValue={[]}
                onChange={(
                  _event: SyntheticEvent<Element, Event>,
                  value: string[],
                ) => {
                  // Uppercase and validate all entries (freeSolo can add lowercase)
                  const processed = value.map((v) => v.toUpperCase());
                  const valid = new Set(
                    processed.filter(
                      (v) => allSymbolsSet.has(v) || watchlistSyms.includes(v),
                    ),
                  );
                  setWatchlistSyms([...valid]);
                  setQuery('');

                  // Show feedback for any invalid entries the user tried to add
                  const newEntries = value.filter(
                    (v) => !watchlistSyms.includes(v),
                  );
                  const invalidEntries = newEntries.filter(
                    (v) => !allSymbolsSet.has(v.toUpperCase()),
                  );
                  if (invalidEntries.length > 0) {
                    setInvalidInput(invalidEntries.join(', '));
                  } else {
                    setInvalidInput('');
                  }
                }}
                noOptionsText={
                  query.length === 0
                    ? 'Type symbols or paste a comma-separated list'
                    : 'No matching symbols'
                }
                onInputChange={(_, val, reason) => {
                  if (reason === 'reset' || reason === 'clear') {
                    setQuery('');
                    return;
                  }
                  // Detect commas in typed input for inline delimiter handling
                  if (val.includes(',')) {
                    parseAndAddSymbols(val);
                    setQuery('');
                    return;
                  }
                  setQuery(val);
                }}
                PaperComponent={({ children }) => (
                  <Paper
                    style={{
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    {children}
                  </Paper>
                )}
                renderTags={() => undefined}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputProps={{
                      ...params.inputProps,
                      onPaste: handlePaste,
                    }}
                    variant="standard"
                    placeholder={
                      watchlistSyms.length === 0
                        ? 'Add symbols (e.g. AAPL, MSFT, GOOG)'
                        : '+ Add symbol'
                    }
                    error={!!invalidInput}
                    helperText={
                      invalidInput ? `Not found: ${invalidInput}` : undefined
                    }
                    sx={{
                      '& .MuiInput-root:before': {
                        borderBottomColor: alpha(
                          theme.palette.primary.main,
                          0.35,
                        ),
                      },
                      '& .MuiInput-root:hover:not(.Mui-disabled):before': {
                        borderBottomColor: theme.palette.primary.main,
                      },
                      '& .MuiInput-root:after': {
                        borderBottomColor: theme.palette.primary.main,
                      },
                    }}
                  />
                )}
              />
            </Box>
          </SortableContext>
        </DndContext>
      </Box>
    </Box>
  );
};
